import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

const ChatBubble = ({ activeChats, user, onOpenChat }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});

  useEffect(() => {
    if (!user || activeChats.length === 0) return;

    const fetchUnread = async () => {
      const { data, error } = await supabase
        .from('citados_messages')
        .select('booking_id')
        .eq('receiver_id', user.id)
        .eq('is_read', false);

      if (!error && data) {
        const counts = {};
        data.forEach(msg => {
          counts[msg.booking_id] = (counts[msg.booking_id] || 0) + 1;
        });
        setUnreadCounts(counts);
      }
    };

    fetchUnread();

    const channel = supabase
      .channel(`chat_bubble_unread_${user.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'citados_messages' },
        (payload) => {
          if (payload.new.receiver_id === user.id && !payload.new.is_read) {
            setUnreadCounts(prev => ({
              ...prev,
              [payload.new.booking_id]: (prev[payload.new.booking_id] || 0) + 1
            }));
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'citados_messages' },
        (payload) => {
          // Supabase Realtime doesn't filter unchanged columns well on UPDATE.
          // Filter manually in JS to ensure we catch the 'is_read' change.
          if (payload.new.receiver_id === user.id) {
            fetchUnread();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, activeChats.length]);

  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);

  if (!user || activeChats.length === 0) return null;

  return (
    <div className="chat-bubble-container" style={{ position: 'fixed', bottom: '30px', right: '30px', zIndex: 2000 }}>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9, transformOrigin: 'bottom right' }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="glass"
            style={{
              position: 'absolute',
              bottom: '80px',
              right: 0,
              width: '300px',
              maxHeight: '400px',
              borderRadius: '1.5rem',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(15, 15, 18, 0.95)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)'
            }}
          >
            <div style={{ padding: '1.2rem', borderBottom: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Chats Activos</h3>
              <button onClick={() => setIsOpen(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem' }}>
              {activeChats.map((chat) => {
                const otherSideName = chat.otherSide?.name || 'Usuario';
                const otherSideImage = chat.otherSide?.image || `https://api.dicebear.com/7.x/initials/svg?seed=${otherSideName}`;
                
                return (
                  <motion.div
                    key={chat.id}
                    whileHover={{ background: 'rgba(255,255,255,0.05)' }}
                    onClick={() => {
                      onOpenChat(chat);
                      setIsOpen(false);
                    }}
                    style={{
                      padding: '0.8rem 1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      cursor: 'pointer',
                      borderRadius: '1rem',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ position: 'relative' }}>
                      <img src={otherSideImage} style={{ width: '40px', height: '40px', borderRadius: '50%', border: '2px solid rgba(205, 255, 0, 0.2)' }} alt={otherSideName} />
                      <div style={{ position: 'absolute', bottom: 0, right: 0, width: '12px', height: '12px', background: '#4ade80', borderRadius: '50%', border: '2px solid #111' }}></div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontWeight: 600, fontSize: '0.9rem', color: unreadCounts[chat.id] ? '#fff' : 'inherit' }}>{otherSideName}</p>
                      <p style={{ margin: 0, fontSize: '0.75rem', color: unreadCounts[chat.id] ? '#4ade80' : 'var(--text-muted)' }}>
                        {unreadCounts[chat.id] ? 'Nuevos mensajes...' : 'Coordinar partido...'}
                      </p>
                    </div>
                    {unreadCounts[chat.id] > 0 && (
                      <div style={{
                        background: '#ef4444',
                        color: 'white',
                        borderRadius: '50%',
                        padding: '2px 8px',
                        fontSize: '0.7rem',
                        fontWeight: 800
                      }}>
                        {unreadCounts[chat.id]}
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="btn-primary"
        style={{
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          boxShadow: '0 8px 25px rgba(205, 255, 0, 0.3)',
          position: 'relative'
        }}
      >
        {isOpen ? <X size={28} /> : <MessageSquare size={28} />}
        {totalUnread > 0 && !isOpen && (
          <div style={{
            position: 'absolute',
            top: '-5px',
            right: '-5px',
            background: '#ef4444',
            color: 'white',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            display: 'grid',
            placeItems: 'center',
            fontSize: '0.75rem',
            fontWeight: 800,
            border: '3px solid #000',
            animation: 'pulse 2s infinite'
          }}>
            {totalUnread}
          </div>
        )}
      </motion.button>
    </div>
  );
};

export default ChatBubble;
